import { elegirPantallaTv } from '../presentacion/presentacionClienteShared';

/**
 * Vista cliente: abre la ventana de presentación idealmente en el segundo monitor (TV).
 * El mando va embebido en el CRM.
 *
 * No usar await antes del primer window.open (bloqueo de popups).
 * Usamos `/presentacion-tv.html` (estático): el primer script del documento pide pantalla completa
 * en el mismo flujo que la navegación por `window.open` (mejor que about:blank + document.write).
 * Tras abrir: Window Management API + moveTo/resizeTo.
 */

const TV_SCREEN_KEY = 'vitalcasa-presentacion-tv-screen';

function readCachedScreen() {
  try {
    const raw = sessionStorage.getItem(TV_SCREEN_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (
      typeof o.left !== 'number' ||
      typeof o.top !== 'number' ||
      typeof o.width !== 'number' ||
      typeof o.height !== 'number'
    ) {
      return null;
    }
    return o;
  } catch {
    return null;
  }
}

function writeCachedScreen(screen) {
  if (!screen) return;
  try {
    sessionStorage.setItem(
      TV_SCREEN_KEY,
      JSON.stringify({
        left: Math.round(screen.left),
        top: Math.round(screen.top),
        width: Math.round(screen.width),
        height: Math.round(screen.height),
      })
    );
  } catch {
    /* modo privado o cuota */
  }
}

function featuresParaPantalla(screen) {
  if (!screen) return undefined;
  const { left, top, width, height } = screen;
  return `left=${Math.round(left)},top=${Math.round(top)},width=${Math.round(width)},height=${Math.round(height)},menubar=no,toolbar=no,scrollbars=no`;
}

function urlLanzadorPresentacionTv(urlPresentacion) {
  const basePath = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
  const origin = window.location.origin;
  const u = encodeURIComponent(urlPresentacion);
  return `${origin}${basePath}/presentacion-tv.html?u=${u}`;
}

function aplicarGeometria(ventana, screen) {
  if (!ventana || !screen) return;
  try {
    ventana.moveTo(Math.round(screen.left), Math.round(screen.top));
    ventana.resizeTo(Math.round(screen.width), Math.round(screen.height));
    writeCachedScreen(screen);
  } catch {
    /* algunos navegadores restringen */
  }
}

async function colocarVentanaEnOtraPantalla(ventana) {
  if (!ventana || typeof window.getScreenDetails !== 'function') return;
  try {
    const sd = await window.getScreenDetails();
    const screens = Array.from(sd.screens);
    const current = sd.currentScreen;
    const target = elegirPantallaTv(screens, current);
    if (!target) return;

    aplicarGeometria(ventana, target);
    requestAnimationFrame(() => aplicarGeometria(ventana, target));
    setTimeout(() => aplicarGeometria(ventana, target), 50);
    setTimeout(() => aplicarGeometria(ventana, target), 250);
  } catch {
    /* sin permiso "Administrar ventanas en todas las pantallas" o API no disponible */
  }
}

/**
 * @param {string} propiedadId UUID de la propiedad en el CRM
 */
export function abrirVistaClientePresentacion(propiedadId) {
  const canal =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `vcp-${Date.now()}`;

  const origin = window.location.origin;
  const urlPantalla = `${origin}/presentacion/${encodeURIComponent(propiedadId)}?canal=${encodeURIComponent(canal)}`;
  const nameP = `vc-pres-${canal}`;

  window.dispatchEvent(
    new CustomEvent('vitalcasa-presentacion-mando', {
      detail: { canal, propiedadId },
    })
  );

  const cached = readCachedScreen();
  const openFeatures = featuresParaPantalla(cached);

  const lanzador = urlLanzadorPresentacionTv(urlPantalla);
  let wPantalla = window.open(lanzador, nameP, openFeatures);

  if (!wPantalla) {
    wPantalla = window.open(urlPantalla, nameP, openFeatures);
  }

  if (!wPantalla) {
    window.alert(
      'No se pudo abrir la ventana de la TV. Permite ventanas emergentes para app.vitalcasa.com (icono en la barra de direcciones) y vuelve a pulsar el icono de la TV en la propiedad.'
    );
    return;
  }

  try {
    wPantalla.focus();
  } catch {
    /* algunos navegadores */
  }

  void colocarVentanaEnOtraPantalla(wPantalla);
}
